import { useRef, useState, useCallback } from 'react';

const AUDIO_URL =
  'https://yksxtgieumaneyxabdsh.supabase.co/storage/v1/object/public/Meditation%20Audio/Stephen%20Orry%20001%20-%2001%20Protector.mp3';
const FADE_OUT_DURATION = 1.5; // seconds

export function useAudio() {
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const gainRef = useRef(null);
  const sourceRef = useRef(null);
  const bufferRef = useRef(null);
  const loadPromiseRef = useRef(null);
  const offsetRef = useRef(0);
  const startedAtRef = useRef(0);
  const fadeTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const unlockedRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);

  // Must be called synchronously inside a user-gesture handler (iOS Safari).
  const initContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;

    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(1, ctx.currentTime);

    // Signal chain: source → analyser → gain → destination
    analyser.connect(gain);
    gain.connect(ctx.destination);

    ctxRef.current = ctx;
    analyserRef.current = analyser;
    gainRef.current = gain;
    return ctx;
  }, []);

  const loadBuffer = useCallback(async (ctx) => {
    if (bufferRef.current) return bufferRef.current;
    if (loadPromiseRef.current) return loadPromiseRef.current;

    loadPromiseRef.current = (async () => {
      let arrayBuffer;
      try {
        const res = await fetch(AUDIO_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        arrayBuffer = await res.arrayBuffer();
      } catch (err) {
        loadPromiseRef.current = null;
        throw new Error(`Fetch: ${err.message}`);
      }

      // Use the callback form of decodeAudioData wrapped in an explicit Promise.
      // Some iOS WebKit builds do not properly reject the Promise-only form.
      let buffer;
      try {
        buffer = await new Promise((resolve, reject) => {
          ctx.decodeAudioData(
            arrayBuffer,
            (decoded) => resolve(decoded),
            (err) => reject(err ?? new Error('decodeAudioData error (no details)'))
          );
        });
      } catch (err) {
        loadPromiseRef.current = null;
        throw new Error(`Decode: ${err?.message ?? String(err)}`);
      }

      bufferRef.current = buffer;
      return buffer;
    })();

    return loadPromiseRef.current;
  }, []);

  // Null onended before stopping to distinguish manual stop from natural end.
  const stopSource = useCallback((source) => {
    if (!source) return;
    source.onended = null;
    try { source.stop(); } catch (_) {}
  }, []);

  const toggle = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      // ── Synchronous section — must stay above any await ──────────────────
      const ctx = initContext();

      // Always call resume() unconditionally on every tap — iOS Chrome/Edge
      // can silently re-suspend between interactions.
      const resumePromise = ctx.resume();

      // Silent-buffer unlock: schedule a 1-sample silent node on first touch.
      if (!unlockedRef.current) {
        unlockedRef.current = true;
        const silentBuf = ctx.createBuffer(1, 1, ctx.sampleRate);
        const unlock = ctx.createBufferSource();
        unlock.buffer = silentBuf;
        unlock.connect(ctx.destination);
        unlock.start(0);
      }
      // ── End synchronous section ──────────────────────────────────────────

      await resumePromise;

      if (isPlaying) {
        // ── Pause: capture offset, fade out, then stop ─────────────────────
        const buf = bufferRef.current;
        offsetRef.current = buf
          ? ((ctx.currentTime - startedAtRef.current) % buf.duration +
              buf.duration) %
            buf.duration
          : 0;

        const gain = gainRef.current;
        const now = ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(0, now + FADE_OUT_DURATION);

        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        const src = sourceRef.current;
        fadeTimerRef.current = setTimeout(() => {
          stopSource(src);
          if (sourceRef.current === src) sourceRef.current = null;
          fadeTimerRef.current = null;
        }, FADE_OUT_DURATION * 1000);

        setIsPlaying(false);
      } else {
        // ── Play (or resume during fade) ────────────────────────────────────
        if (fadeTimerRef.current) {
          clearTimeout(fadeTimerRef.current);
          fadeTimerRef.current = null;
        }
        stopSource(sourceRef.current);
        sourceRef.current = null;

        // Explicitly reset gain to 1 before playback.
        const gain = gainRef.current;
        gain.gain.cancelScheduledValues(ctx.currentTime);
        gain.gain.setValueAtTime(1, ctx.currentTime);

        setIsLoading(true);
        setHasEnded(false);
        const buffer = await loadBuffer(ctx);
        setIsLoading(false);

        // Verify signal chain nodes are present before connecting.
        if (!analyserRef.current || !gainRef.current) {
          throw new Error('Signal chain nodes missing (analyser or gain is null)');
        }

        const source = ctx.createBufferSource();
        source.buffer = buffer;
        // source → analyser → gain → destination
        source.connect(analyserRef.current);

        source.onended = () => {
          offsetRef.current = 0;
          sourceRef.current = null;
          setIsPlaying(false);
          setHasEnded(true);
        };

        const offset = offsetRef.current % buffer.duration;
        source.start(0, offset);
        startedAtRef.current = ctx.currentTime - offset;
        sourceRef.current = source;

        setIsPlaying(true);
      }
    } catch (err) {
      console.error('Audio toggle error:', err);
      setIsLoading(false);
    } finally {
      inFlightRef.current = false;
    }
  }, [isPlaying, initContext, loadBuffer, stopSource]);

  return { toggle, isPlaying, isLoading, analyserRef, hasEnded };
}
